import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import { Plus } from '../../../assets';
import { SummaryStructured, StructuredTask } from '../../../services/api.service';
import { parseProtocolMarkdown } from '../../../utils/parse-protocol';
import { priorityBadge } from '../../../utils/tech-terms';
import { formatTaskDeadline, DEFAULT_TASK_DEADLINE } from '../../../utils/task-deadline';
import CreateTasksModal from './create-tasks-modal.component';
import modalClasses from './create-tasks-modal.module.scss';

const cn = createCn('file', classes);

export type { StructuredTask };

const PRIORITY_OPTIONS = ['Высокий', 'Средний', 'Низкий'];

function capitalizeTask(text: string): string {
  const t = text.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function taskKey(text: string): string {
  return text.toLowerCase().replace(/[^\wа-яё\s]/gi, '').replace(/\s+/g, ' ').trim().slice(0, 48);
}

function parseSummaryFallback(items: string[]): StructuredTask[] {
  const rows: StructuredTask[] = [];
  const seen = new Set<string>();
  items.forEach(raw => {
    const text = String(raw || '').trim();
    if (!text) return;
    const dash = text.indexOf(' — ');
    let task = text;
    let assignee = '—';
    if (dash > 0) {
      task = text.slice(0, dash).trim();
      assignee = text.slice(dash + 3).trim() || '—';
    }
    task = capitalizeTask(task);
    const key = taskKey(task);
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push({ task, assignee, deadline: DEFAULT_TASK_DEADLINE, priority: 'Средний' });
  });
  return rows;
}

function resolveTasks(
  protocol?: string,
  structuredTasks?: StructuredTask[],
  summaryStructured?: SummaryStructured | null,
): StructuredTask[] {
  if (structuredTasks?.length) {
    return structuredTasks.map(t => ({
      ...t,
      task: capitalizeTask(t.task),
      assignee: t.assignee?.trim() || '—',
    }));
  }

  if (protocol?.trim()) {
    const parsed = parseProtocolMarkdown(protocol);
    if (parsed.tasks.length) {
      const seen = new Set<string>();
      return parsed.tasks
        .map(t => ({
          task: capitalizeTask(t.task),
          assignee: t.assignee?.trim() || '—',
          deadline: formatTaskDeadline(t.deadline),
          priority: t.priority?.trim() || 'Средний',
        }))
        .filter(t => {
          const key = taskKey(t.task);
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    }
  }

  return parseSummaryFallback(summaryStructured?.action_items || []);
}

type Props = {
  protocol?: string;
  structuredTasks?: StructuredTask[];
  summaryStructured?: SummaryStructured | null;
  editing?: boolean;
  editTasks?: StructuredTask[];
  onEditTasksChange?: (tasks: StructuredTask[]) => void;
};

const MeetingTasksPanel = ({
  protocol,
  structuredTasks,
  summaryStructured,
  editing = false,
  editTasks = [],
  onEditTasksChange,
}: Props) => {
  const { t } = useTranslation();
  // Панель рендерится только на странице записи (/files/:id) — id встречи берём из роута,
  // чтобы не менять пропсы в file.page.tsx (его правит другой агент).
  const { id: meetingId } = useParams<{ id: string }>();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const tasks = useMemo(
    () => resolveTasks(protocol, structuredTasks, summaryStructured),
    [protocol, structuredTasks, summaryStructured],
  );

  const displayTasks = editing ? editTasks : tasks;

  const updateTask = (index: number, patch: Partial<StructuredTask>) => {
    if (!onEditTasksChange) return;
    onEditTasksChange(
      editTasks.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  const removeTask = (index: number) => {
    onEditTasksChange?.(editTasks.filter((_, i) => i !== index));
  };

  const addTask = () => {
    onEditTasksChange?.([
      ...editTasks,
      { task: '', assignee: '—', deadline: DEFAULT_TASK_DEADLINE, priority: 'Средний' },
    ]);
  };

  if (!displayTasks.length && !editing) {
    return (
      <div className={cn('tasks-panel')}>
        <p className={cn('empty')}>
          Поручений пока нет. Сгенерируйте протокол — задачи появятся из раздела 4.
        </p>
      </div>
    );
  }

  if (editing) {
    return (
      <div className={cn('tasks-panel', { editing: true })}>
        <p className={cn('section-edit-hint')}>
          Измените поручения вручную. Пустые строки не сохраняются.
        </p>
        <table className={cn('protocol-table', { tasks: true, editing: true })}>
          <thead>
            <tr>
              <th>№</th>
              <th>Задача</th>
              <th>Ответственный</th>
              <th>Срок</th>
              <th>Приоритет</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {displayTasks.map((task, i) => (
              <tr key={`edit-${i}`}>
                <td>{i + 1}</td>
                <td>
                  <input
                    type="text"
                    className={cn('section-edit-input')}
                    value={task.task}
                    onChange={(e) => updateTask(i, { task: e.target.value })}
                    placeholder="Описание поручения"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className={cn('section-edit-input')}
                    value={task.assignee}
                    onChange={(e) => updateTask(i, { assignee: e.target.value })}
                    placeholder="ФИО"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className={cn('section-edit-input')}
                    value={task.deadline}
                    onChange={(e) => updateTask(i, { deadline: e.target.value })}
                    placeholder="Срок"
                  />
                </td>
                <td>
                  <select
                    className={cn('section-edit-select')}
                    value={task.priority || 'Средний'}
                    onChange={(e) => updateTask(i, { priority: e.target.value })}
                  >
                    {PRIORITY_OPTIONS.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <button
                    type="button"
                    className={cn('section-edit-remove')}
                    onClick={() => removeTask(i)}
                    aria-label="Удалить"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className={cn('section-edit-add')} onClick={addTask}>
          + Добавить поручение
        </button>
      </div>
    );
  }

  return (
    <div className={cn('tasks-panel')}>
      <div className={modalClasses['panel-header']}>
        <p className={cn('tasks-intro')}>
          {displayTasks.length}{' '}
          {displayTasks.length === 1 ? 'поручение' : displayTasks.length < 5 ? 'поручения' : 'поручений'}
          {' '}из протокола совещания
        </p>
        {meetingId && displayTasks.length > 0 && (
          <button
            type="button"
            className={modalClasses['create-btn']}
            onClick={() => setCreateModalOpen(true)}
          >
            <Plus />
            {t('meeting-tasks.create-tasks')}
          </button>
        )}
      </div>
      {meetingId && (
        <CreateTasksModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          meetingId={meetingId}
          sourceTasks={displayTasks}
        />
      )}
      <table className={cn('protocol-table', { tasks: true })}>
        <thead>
          <tr>
            <th>№</th>
            <th>Задача</th>
            <th>Ответственный</th>
            <th>Срок</th>
            <th>Приоритет</th>
          </tr>
        </thead>
        <tbody>
          {displayTasks.map((task, i) => {
            const badge = priorityBadge(task.priority);
            return (
              <tr key={`${taskKey(task.task)}-${i}`}>
                <td>{i + 1}</td>
                <td className={cn('protocol-task-cell')}>{task.task}</td>
                <td>{task.assignee}</td>
                <td>{formatTaskDeadline(task.deadline)}</td>
                <td>
                  <span className={cn('protocol-priority', { [badge.className]: true })}>
                    {badge.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MeetingTasksPanel;
